import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  FiRefreshCw,
  FiAlertTriangle,
  FiInbox,
  FiDollarSign,
  FiFilter,
  FiRadio,
  FiDownload,
  FiTrash2,
} from 'react-icons/fi';
import { MdPix } from 'react-icons/md';
import { FaBarcode } from 'react-icons/fa';
import Layout from '../components/Layout';
import theme from '../styles/theme';
import { contratosAPI } from '../services/api';
import toast from 'react-hot-toast';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[4]};
  flex-wrap: wrap;
`;

const HeaderGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Title = styled.h1`
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: ${theme.fontSizes.md};
  color: ${theme.colors.text.muted};
  margin: 0;
`;

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  flex-wrap: wrap;
`;

const GhostButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  border-radius: ${theme.borderRadius.xl};
  border: 1px solid ${theme.colors.border.primary};
  background: transparent;
  color: ${theme.colors.text.secondary};
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.primary[500]};
    color: ${theme.colors.primary[600]};
  }
`;

const Select = styled.select`
  padding: ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }
`;

const Card = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius['2xl']};
  padding: ${theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[3]};
  flex-wrap: wrap;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: ${theme.fontSizes.lg};
  font-weight: 700;
  color: ${theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const CycleDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: ${theme.spacing[4]};

  @media (max-width: 1400px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const MetricCard = styled.div`
  background: ${theme.colors.background.primary};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  padding: ${theme.spacing[4]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
`;

const MetricLabel = styled.span`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.sm};
`;

const MetricValue = styled.span`
  font-size: ${theme.fontSizes.lg};
  font-weight: 700;
  color: ${theme.colors.text.primary};
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  overflow-y: visible;
  width: 100%;
  max-width: 100%;
  
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${theme.colors.background.muted};
    border-radius: ${theme.borderRadius.full};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.border.primary};
    border-radius: ${theme.borderRadius.full};
    
    &:hover {
      background: ${theme.colors.text.muted};
    }
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 500px;
  
  @media (max-width: ${theme.breakpoints.md}) {
    min-width: 400px;
  }
`;

const Th = styled.th`
  text-align: left;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  color: ${theme.colors.text.secondary};
  border-bottom: 1px solid ${theme.colors.border.primary};
`;

const Td = styled.td`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-bottom: 1px solid ${theme.colors.border.primary};
  font-size: ${theme.fontSizes.base};
  color: ${theme.colors.text.secondary};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[10]} ${theme.spacing[6]};
  text-align: center;
  border: 1px dashed ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius['2xl']};
  color: ${theme.colors.text.muted};
`;

const EmptyIcon = styled.div`
  font-size: ${theme.fontSizes['4xl']};
  color: ${theme.colors.text.muted};
  opacity: 0.5;
`;

const Alert = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.error[500]};
  background: ${theme.colors.error[100]};
  color: ${theme.colors.error[700]};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.xs};
  font-weight: 700;
  text-transform: uppercase;
  background: ${({ $status }) => {
    const normalized = ($status || '').toUpperCase();
    if (normalized === 'CONFIRMADO' || normalized === 'RECEIVED' || normalized === 'CONFIRMED') {
      return theme.colors.success[100];
    }
    if (normalized === 'OVERDUE') return theme.colors.error[100];
    return theme.colors.warning[100];
  }};
  color: ${({ $status }) => {
    const normalized = ($status || '').toUpperCase();
    if (normalized === 'CONFIRMADO' || normalized === 'RECEIVED' || normalized === 'CONFIRMED') {
      return theme.colors.success[700];
    }
    if (normalized === 'OVERDUE') return theme.colors.error[700];
    return theme.colors.warning[700];
  }};
`;

const TableActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  flex-wrap: nowrap;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: ${theme.fontSizes.base};

  &:hover:not(:disabled) {
    border-color: ${theme.colors.primary[500]};
    color: ${theme.colors.primary[600]};
    background: ${theme.colors.primary[50]};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${theme.zIndex.modal};
`;

const ModalContent = styled.div`
  width: 100%;
  max-width: 520px;
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius['2xl']};
  border: 1px solid ${theme.colors.border.primary};
  box-shadow: ${theme.shadows.lg};
  padding: ${theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: ${theme.fontSizes.lg};
  font-weight: 700;
  color: ${theme.colors.text.primary};
`;

const ModalMessage = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.base};
  line-height: 1.5;
  margin: 0;
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  justify-content: flex-end;
`;

const ModalButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border: 1px solid ${(props) => (props.$danger ? theme.colors.error[500] : theme.colors.border.primary)};
  background: ${(props) => (props.$danger ? theme.colors.error[500] : 'transparent')};
  color: ${(props) => (props.$danger ? theme.colors.text.primary : theme.colors.text.secondary)};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${(props) => (props.$danger ? theme.colors.error[600] : theme.colors.background.secondary)};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ClienteSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[5]};
  padding: ${theme.spacing[5]};
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius['2xl']};
`;

const ClienteHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding-bottom: ${theme.spacing[3]};
  border-bottom: 2px solid ${theme.colors.primary[500]};
`;

const ClienteNome = styled.h2`
  margin: 0;
  font-size: ${theme.fontSizes.xl};
  font-weight: 700;
  color: ${theme.colors.primary[400]};
`;

const ContratoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const ContratoHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding-bottom: ${theme.spacing[2]};
  border-bottom: 1px solid ${theme.colors.border.primary};
`;

const ContratoNome = styled.h3`
  margin: 0;
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
  color: ${theme.colors.text.primary};
`;

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR');
};

const formatContratoValido = (dataInicio, dataFim) => {
  if (!dataInicio) return 'Contrato';
  const inicio = formatDate(dataInicio);
  if (!dataFim) {
    return `Contrato válido de ${inicio}`;
  }
  const fim = formatDate(dataFim);
  return `Contrato válido de ${inicio} a ${fim}`;
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(value || 0);

const getCobrancaStatusLabel = (status) => {
  switch ((status || 'PENDING').toUpperCase()) {
    case 'RECEIVED':
    case 'CONFIRMED':
    case 'CONFIRMADO':
      return 'Pago';
    case 'OVERDUE':
      return 'Atrasado';
    default:
      return 'Pendente';
  }
};

const extrairPayloadPix = (pixQr) => {
  if (!pixQr) return null;
  if (typeof pixQr === 'string') return pixQr;
  if (typeof pixQr === 'object' && pixQr.payload) return pixQr.payload;
  return null;
};

const copiarConteudo = async (texto, label) => {
  if (!texto) return;
  try {
    await navigator.clipboard.writeText(texto);
    toast.success(`${label} copiado!`);
  } catch (error) {
    toast.error('Não foi possível copiar automaticamente. Copie manualmente.');
  }
};

const baixarPDF = (url) => {
  if (!url) return;
  window.open(url, '_blank');
};

const DashboardCliente = () => {
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [dados, setDados] = useState(null);
  const [clienteSelecionado, setClienteSelecionado] = useState('');
  const [modalExcluirCobranca, setModalExcluirCobranca] = useState({
    aberto: false,
    cobranca: null,
    excluindo: false,
  });

  const carregarDados = async (silent = false, clienteId = null) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      setErro(null);
      const response = await contratosAPI.dashboardMeusContratos(clienteId);
      if (response.data?.success) {
        setDados(response.data.data);
      } else {
        setErro('Não foi possível carregar os dados.');
      }
    } catch (error) {
      console.error('Erro ao carregar dashboard do cliente', error);
      setErro('Erro ao carregar dashboard do cliente.');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    carregarDados();
  }, []);

  useEffect(() => {
    if (dados) {
      carregarDados(true, clienteSelecionado || null);
    }
  }, [clienteSelecionado]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        carregarDados(true, clienteSelecionado || null);
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [clienteSelecionado]);

  const fecharModalExcluirCobranca = () => {
    setModalExcluirCobranca({
      aberto: false,
      cobranca: null,
      excluindo: false,
    });
  };

  const clientesLista = dados?.clientes || [];
  const cobrancasCiclo = dados?.cobrancasCiclo || [];
  const cobrancasExtensao = dados?.cobrancasExtensao || [];

  // Agrupar cobranças por cliente e depois por contrato
  const dadosPorCliente = useMemo(() => {
    const mapaClientes = new Map();

    // Criar mapa de clientes
    clientesLista.forEach((cliente) => {
      mapaClientes.set(cliente.id, {
        cliente_id: cliente.id,
        cliente_nome: cliente.nome,
        contratos: new Map(),
      });
    });

    // Primeiro, criar todos os ciclos a partir das cobranças de ciclo
    cobrancasCiclo.forEach((cobranca) => {
      const clienteId = cobranca.cliente_id;
      const contratoId = cobranca.contrato_id;

      // Garantir que o cliente existe no mapa
      if (!mapaClientes.has(clienteId)) {
        mapaClientes.set(clienteId, {
          cliente_id: clienteId,
          cliente_nome: `Cliente #${clienteId}`,
          contratos: new Map(),
        });
      }

      const cliente = mapaClientes.get(clienteId);
      if (!cliente.contratos.has(contratoId)) {
        cliente.contratos.set(contratoId, {
          contrato_id: contratoId,
          contrato_nome: cobranca.contrato_nome,
          contrato_data_inicio: cobranca.contrato_data_inicio,
          contrato_data_fim: cobranca.contrato_data_fim,
          ciclos: new Map(),
        });
      }
      
      // Adicionar ciclo com suas extensões (inicialmente vazias)
      const cicloId = cobranca.contrato_ciclo_id;
      const contrato = cliente.contratos.get(contratoId);
      if (!contrato.ciclos.has(cicloId)) {
        contrato.ciclos.set(cicloId, {
          ...cobranca,
          extensoes: [],
        });
      }
    });

    // Depois, adicionar todas as extensões aos ciclos correspondentes
    cobrancasExtensao.forEach((cobranca) => {
      const clienteId = cobranca.cliente_id;
      const contratoId = cobranca.contrato_id;
      const cicloId = cobranca.contrato_ciclo_id;

      // Se não tem ciclo associado, ignorar
      if (!cicloId) return;

      // Garantir que o cliente existe no mapa
      if (!mapaClientes.has(clienteId)) {
        mapaClientes.set(clienteId, {
          cliente_id: clienteId,
          cliente_nome: `Cliente #${clienteId}`,
          contratos: new Map(),
        });
      }

      const cliente = mapaClientes.get(clienteId);
      if (!cliente.contratos.has(contratoId)) {
        cliente.contratos.set(contratoId, {
          contrato_id: contratoId,
          contrato_nome: cobranca.contrato_nome,
          contrato_data_inicio: cobranca.contrato_data_inicio,
          contrato_data_fim: cobranca.contrato_data_fim,
          ciclos: new Map(),
        });
      }

      const contrato = cliente.contratos.get(contratoId);
      
      // Se o ciclo já existe (criado a partir de cobrança de ciclo), adicionar extensão
      if (contrato.ciclos.has(cicloId)) {
        contrato.ciclos.get(cicloId).extensoes.push(cobranca);
      } else {
        // Se o ciclo não existe ainda, criar um ciclo apenas com extensões
        // (caso raro: extensão sem cobrança de ciclo, mas ainda assim agrupar por ciclo)
        contrato.ciclos.set(cicloId, {
          id: null,
          cliente_id: clienteId,
          contrato_id: contratoId,
          contrato_ciclo_id: cicloId,
          contrato_nome: cobranca.contrato_nome,
          contrato_data_inicio: cobranca.contrato_data_inicio,
          contrato_data_fim: cobranca.contrato_data_fim,
          ciclo_inicio: cobranca.ciclo_inicio || null,
          ciclo_fim: cobranca.ciclo_fim || null,
          valor: null,
          due_date: null,
          status: null,
          extensoes: [cobranca],
        });
      }
    });

    // Converter para array e filtrar clientes sem cobranças
    return Array.from(mapaClientes.values())
      .filter((cliente) => cliente.contratos.size > 0)
      .map((cliente) => ({
        ...cliente,
        contratos: Array.from(cliente.contratos.values()).map((contrato) => ({
          ...contrato,
          ciclos: Array.from(contrato.ciclos.values()),
        })),
      }));
  }, [clientesLista, cobrancasCiclo, cobrancasExtensao]);

  const mostrarFiltroCliente = clientesLista.length > 1;

  if (loading) {
    return (
      <Layout title="Painel de Pagamentos">
        <Container>
          <Card>
            <Subtitle>Carregando informações...</Subtitle>
          </Card>
        </Container>
      </Layout>
    );
  }

  if (erro) {
    return (
      <Layout title="Painel de Pagamentos">
        <Container>
          <Card>
            <Alert>
              <FiAlertTriangle />
              {erro}
            </Alert>
            <GhostButton onClick={() => carregarDados(false)}>
              <FiRefreshCw />
              Tentar novamente
            </GhostButton>
          </Card>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title="Painel de Pagamentos">
      <Container>
        {mostrarFiltroCliente && (
          <Header>
            <HeaderGroup>
              <Title>Painel de Pagamentos</Title>
              <Subtitle>Consulte suas mensalidades e faturas de extensões.</Subtitle>
            </HeaderGroup>
            <ActionBar>
              <Select
                value={clienteSelecionado}
                onChange={(e) => setClienteSelecionado(e.target.value)}
                style={{ minWidth: '200px' }}
              >
                <option value="">Todos os clientes</option>
                {clientesLista.map((cliente) => (
                  <option key={cliente.id} value={cliente.id}>
                    {cliente.nome}
                  </option>
                ))}
              </Select>
              <GhostButton onClick={() => carregarDados(false, clienteSelecionado || null)}>
                <FiRefreshCw />
                Atualizar
              </GhostButton>
            </ActionBar>
          </Header>
        )}

        {dadosPorCliente.length === 0 ? (
          <Card>
            <EmptyState>
              <EmptyIcon>
                <FiInbox />
              </EmptyIcon>
              <span>Nenhuma cobrança encontrada.</span>
            </EmptyState>
          </Card>
        ) : (
          dadosPorCliente.map((cliente) => (
            <ClienteSection key={cliente.cliente_id}>
              <ClienteHeader>
                <ClienteNome>{cliente.cliente_nome}</ClienteNome>
              </ClienteHeader>

              {cliente.contratos.map((contrato) => (
                <ContratoSection key={contrato.contrato_id}>
                  <ContratoHeader>
                    <ContratoNome>
                      {formatContratoValido(contrato.contrato_data_inicio, contrato.contrato_data_fim)}
                    </ContratoNome>
                  </ContratoHeader>

                  {/* Cobranças do Ciclo */}
                  {contrato.ciclos
                    .filter((ciclo) => ciclo.ciclo_inicio && ciclo.ciclo_fim) // Só mostrar ciclos com datas válidas
                    .sort((a, b) => {
                      // Ordenar por data de início (mais recente primeiro)
                      const dateA = new Date(a.ciclo_inicio);
                      const dateB = new Date(b.ciclo_inicio);
                      return dateB - dateA;
                    })
                    .map((ciclo) => (
                    <Card key={ciclo.id || ciclo.contrato_ciclo_id}>
                      <CardHeader>
                        <CardTitle>
                          <FiRadio />
                          Ciclo {formatDate(ciclo.ciclo_inicio)} - {formatDate(ciclo.ciclo_fim)}
                        </CardTitle>
                      </CardHeader>

                      {/* Cobrança do ciclo */}
                      {ciclo.id && (
                        <>
                          <CardHeader style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                            <CardTitle>
                              <FiDollarSign />
                              Cobrança do ciclo
                            </CardTitle>
                          </CardHeader>
                          <TableWrapper>
                            <Table>
                              <thead>
                                <tr>
                                  <Th>Data</Th>
                                  <Th>Créditos</Th>
                                  <Th>Valor</Th>
                                  <Th>Vencimento</Th>
                                  <Th>Status</Th>
                                  <Th>Ações</Th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <Td>{formatDate(ciclo.created_at)}</Td>
                                  <Td>{ciclo.creditos_base || 0}</Td>
                                  <Td>{formatCurrency(ciclo.valor)}</Td>
                                  <Td>{formatDate(ciclo.due_date)}</Td>
                                  <Td>
                                    <StatusBadge $status={ciclo.status}>
                                      {getCobrancaStatusLabel(ciclo.status)}
                                    </StatusBadge>
                                  </Td>
                                  <Td>
                                    <TableActions>
                                      {(() => {
                                        const statusCobranca = (ciclo.status || 'PENDING').toUpperCase();
                                        const pagamentoConfirmado = statusCobranca === 'RECEIVED' || statusCobranca === 'CONFIRMED' || statusCobranca === 'CONFIRMADO';
                                        
                                        return (
                                          <>
                                            <ActionButton
                                              onClick={() => baixarPDF(ciclo.bank_slip_url)}
                                              disabled={pagamentoConfirmado || !ciclo.bank_slip_url}
                                              title={pagamentoConfirmado ? 'Pagamento já confirmado' : 'Baixar PDF do boleto'}
                                            >
                                              <FiDownload />
                                            </ActionButton>
                                            <ActionButton
                                              onClick={() => copiarConteudo(ciclo.boleto_linha_digitavel, 'Código de barras')}
                                              disabled={pagamentoConfirmado || !ciclo.boleto_linha_digitavel}
                                              title={pagamentoConfirmado ? 'Pagamento já confirmado' : 'Copiar código de barras do boleto'}
                                            >
                                              <FaBarcode />
                                            </ActionButton>
                                            <ActionButton
                                              onClick={() => {
                                                const pixPayload = extrairPayloadPix(ciclo.pix_qr_code) || ciclo.pix_payload;
                                                copiarConteudo(pixPayload, 'Código Pix');
                                              }}
                                              disabled={pagamentoConfirmado || (!extrairPayloadPix(ciclo.pix_qr_code) && !ciclo.pix_payload)}
                                              title={pagamentoConfirmado ? 'Pagamento já confirmado' : 'Copiar código Pix'}
                                            >
                                              <MdPix />
                                            </ActionButton>
                                          </>
                                        );
                                      })()}
                                    </TableActions>
                                  </Td>
                                </tr>
                              </tbody>
                            </Table>
                          </TableWrapper>
                        </>
                      )}

                      {/* Extensões do ciclo */}
                      {ciclo.extensoes && ciclo.extensoes.length > 0 && (
                        <>
                          <CardHeader style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                            <CardTitle>
                              <FiFilter />
                              Extensões
                            </CardTitle>
                          </CardHeader>
                          <TableWrapper>
                            <Table>
                              <thead>
                                <tr>
                                  <Th>Data</Th>
                                  <Th>Créditos</Th>
                                  <Th>Valor</Th>
                                  <Th>Vencimento</Th>
                                  <Th>Status</Th>
                                  <Th>Ações</Th>
                                </tr>
                              </thead>
                              <tbody>
                                {ciclo.extensoes.map((extensao) => {
                                  const statusCobranca = (extensao.status || 'PENDING').toUpperCase();
                                  const pagamentoConfirmado = statusCobranca === 'RECEIVED' || statusCobranca === 'CONFIRMED' || statusCobranca === 'CONFIRMADO';

                                  return (
                                    <tr key={extensao.id}>
                                      <Td>{formatDate(extensao.created_at)}</Td>
                                      <Td>{extensao.creditos_extensao}</Td>
                                      <Td>{formatCurrency(extensao.valor)}</Td>
                                      <Td>{formatDate(extensao.due_date)}</Td>
                                      <Td>
                                        <StatusBadge $status={extensao.status}>
                                          {getCobrancaStatusLabel(extensao.status)}
                                        </StatusBadge>
                                      </Td>
                                      <Td>
                                        <TableActions>
                                          <ActionButton
                                            onClick={() => baixarPDF(extensao.bank_slip_url)}
                                            disabled={pagamentoConfirmado || !extensao.bank_slip_url}
                                            title={pagamentoConfirmado ? 'Pagamento já confirmado' : 'Baixar PDF do boleto'}
                                          >
                                            <FiDownload />
                                          </ActionButton>
                                          <ActionButton
                                            onClick={() => copiarConteudo(extensao.boleto_linha_digitavel, 'Código de barras')}
                                            disabled={pagamentoConfirmado || !extensao.boleto_linha_digitavel}
                                            title={pagamentoConfirmado ? 'Pagamento já confirmado' : 'Copiar código de barras do boleto'}
                                          >
                                            <FaBarcode />
                                          </ActionButton>
                                          <ActionButton
                                            onClick={() => {
                                              const pixPayload = extrairPayloadPix(extensao.pix_qr_code) || extensao.pix_payload;
                                              copiarConteudo(pixPayload, 'Código Pix');
                                            }}
                                            disabled={pagamentoConfirmado || (!extrairPayloadPix(extensao.pix_qr_code) && !extensao.pix_payload)}
                                            title={pagamentoConfirmado ? 'Pagamento já confirmado' : 'Copiar código Pix'}
                                          >
                                            <MdPix />
                                          </ActionButton>
                                          <ActionButton
                                            onClick={() => {
                                              if (!extensao.id) {
                                                toast.error('Cobrança não encontrada');
                                                return;
                                              }
                                              setModalExcluirCobranca({
                                                aberto: true,
                                                cobranca: extensao,
                                                excluindo: false,
                                              });
                                            }}
                                            disabled={
                                              (statusCobranca !== 'PENDING' && statusCobranca !== 'OVERDUE') ||
                                              !extensao.id
                                            }
                                            title={
                                              statusCobranca !== 'PENDING' && statusCobranca !== 'OVERDUE'
                                                ? 'Apenas cobranças pendentes ou atrasadas podem ser excluídas'
                                                : 'Excluir cobrança'
                                            }
                                          >
                                            <FiTrash2 />
                                          </ActionButton>
                                        </TableActions>
                                      </Td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>
                          </TableWrapper>
                        </>
                      )}
                    </Card>
                  ))}
                </ContratoSection>
              ))}
            </ClienteSection>
          ))
        )}

        {/* Modal de confirmação de exclusão de cobrança */}
        {modalExcluirCobranca.aberto && (
          <ModalOverlay onClick={fecharModalExcluirCobranca}>
            <ModalContent onClick={(event) => event.stopPropagation()}>
              <ModalTitle style={{ color: theme.colors.error[500] }}>Excluir Cobrança</ModalTitle>
              <ModalMessage>
                Tem certeza que deseja excluir esta cobrança de extensão? Esta ação não pode ser desfeita e irá excluir:
                <br /><br />
                • A cobrança de extensão
                <br />
                • O pagamento no Asaas
                <br />
                • Todos os dados relacionados
              </ModalMessage>
              <ModalActions>
                <ModalButton onClick={fecharModalExcluirCobranca} disabled={modalExcluirCobranca.excluindo}>
                  Cancelar
                </ModalButton>
                <ModalButton
                  $danger
                  onClick={async () => {
                    if (!modalExcluirCobranca.cobranca?.id) {
                      toast.error('Cobrança não encontrada');
                      return;
                    }

                    setModalExcluirCobranca((prev) => ({ ...prev, excluindo: true }));

                    try {
                      console.log('[EXTENSAO] Excluindo cobrança', {
                        cobrancaId: modalExcluirCobranca.cobranca.id,
                        asaasPaymentId: modalExcluirCobranca.cobranca.asaas_payment_id,
                        status: modalExcluirCobranca.cobranca.status,
                      });
                      await contratosAPI.excluirCobrancaExtensao(modalExcluirCobranca.cobranca.id);
                      console.log('[EXTENSAO] Cobrança excluída com sucesso');
                      toast.success('Cobrança excluída com sucesso!');
                      fecharModalExcluirCobranca();
                      // Recarregar dados após exclusão
                      await carregarDados(false, clienteSelecionado || null);
                    } catch (error) {
                      console.error('Erro ao excluir cobrança', {
                        error,
                        response: error.response,
                        data: error.response?.data,
                        status: error.response?.status,
                      });
                      const errorMessage =
                        error.response?.data?.error ||
                        error.response?.data?.details ||
                        error.message ||
                        'Erro ao excluir cobrança';
                      toast.error(errorMessage);
                      setModalExcluirCobranca((prev) => ({ ...prev, excluindo: false }));
                    }
                  }}
                  disabled={modalExcluirCobranca.excluindo}
                >
                  <FiTrash2 />
                  {modalExcluirCobranca.excluindo ? 'Excluindo...' : 'Excluir'}
                </ModalButton>
              </ModalActions>
            </ModalContent>
          </ModalOverlay>
        )}
      </Container>
    </Layout>
  );
};

export default DashboardCliente;
